import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import * as monthUtils from '@actual-app/core/shared/months';

import { useLlegadas } from '#components/mobile/budget/travesia/ProveedorLlegadas';
import { useNoche } from '#components/mobile/budget/travesia/useNoche';
import { useRecuerdos } from '#components/mobile/budget/useRecuerdos';
import { MobileBackButton } from '#components/mobile/MobileBackButton';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { MobilePageHeader, Page } from '#components/Page';
import { useNavigate } from '#hooks/useNavigate';
import { useReducedMotion } from '#hooks/useReducedMotion';

import { Escritorio } from './Escritorio';
import type { EnCurso } from './PaginasCuaderno';

/** Madera del escritorio (fondo de toda la pantalla), de día y de noche. */
const MADERA = { dia: '#3b2f25', noche: '#1a140f' } as const;

/**
 * «Tu bitácora»: el escritorio del armador del barco abierto (una bitácora
 * por presupuesto). Repisa con las botellas abiertas, cuaderno con la hoja
 * de totales, una página por travesía cumplida y las que siguen navegando,
 * y el cajón «Archivadas». `?rid=` abre el cuaderno en esa travesía.
 */
export function BitacoraPage({
  vistaPrevia = false,
}: {
  /** La enseña la ayuda de Gavi sobre Ahorros: sin «atrás» y sin abrir nada. */
  vistaPrevia?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recuerdos = useRecuerdos();
  const { travesias, pendientes, ocultas, abrir } = useLlegadas();
  const noche = useNoche();
  const reducido = useReducedMotion();
  const hoy = monthUtils.currentDay();

  const porAbrir = new Set(pendientes.map(r => r.base.rid));
  const enCurso = new Map<string, EnCurso>();
  for (const v of travesias.values()) {
    enCurso.set(v.rid, { saldo: v.saldo, meta: v.objetivo.importe });
  }

  return (
    <Page
      padding={0}
      style={{ backgroundColor: noche ? MADERA.noche : MADERA.dia }}
      header={
        <MobilePageHeader
          title={t('Your logbook')}
          leftContent={vistaPrevia ? undefined : <MobileBackButton />}
        />
      }
    >
      <div
        data-testid="bitacora"
        style={{ paddingBottom: MOBILE_NAV_HEIGHT, flexShrink: 0 }}
      >
        <Escritorio
          key={searchParams.get('rid') ?? ''}
          recuerdos={recuerdos}
          ocultas={ocultas}
          hoy={hoy}
          noche={noche}
          reducido={reducido}
          porAbrir={porAbrir}
          enCurso={enCurso}
          ridInicial={searchParams.get('rid')}
          onAbrirBotella={vistaPrevia ? () => undefined : abrir}
          onIrCategoria={
            vistaPrevia
              ? () => undefined
              : id => void navigate(`/categories/${id}/ficha`)
          }
        />
      </div>
    </Page>
  );
}
